import Image from "next/image";
import Link from "next/link";
import type { GetStaticPaths, GetStaticProps } from "next";
import { allWordpressPosts, type WordpressPost } from "contentlayer/generated";
import BlogServiceCta from "../../components/BlogServiceCta";
import Seo from "../../components/Seo";
import { buildExcerpt, formatCategoryLabel } from "../../lib/posts";

type PostPageProps = {
  post: WordpressPost;
};

export default function BlogPostPage({ post }: PostPageProps) {
  const siteUrl = (process.env.NEXT_PUBLIC_SITE_URL ?? "https://www.kmcpaccounting.com").replace(/\/$/, "");
  const postPath = `/blog/${post.seoSlug ?? post.slug}`;
  const canonicalUrl = `${siteUrl}${postPath}`;
  const description = post.metaDescription && post.metaDescription.trim().length > 0 ? post.metaDescription.trim() : buildExcerpt(post, 180);
  const categoryLabel = formatCategoryLabel(post.primaryCategory);
  const heroImage = post.heroImage || "";
  const heroAlt = post.heroImageAlt || post.title;
  const leadParagraphHtml = post.leadParagraph?.trim() ?? "";
  const leadParagraphText = post.leadParagraphText?.trim() ?? "";
  const leadParagraphFallback = !leadParagraphHtml && !leadParagraphText ? buildExcerpt(post, null) : "";
  const articleHtml = post.localizedContent ?? post.content;
  const firstH2Index = articleHtml.search(/<h2\b/i);
  const contentBeforeFirstH2 = firstH2Index >= 0 ? articleHtml.slice(0, firstH2Index) : "";
  const contentFromFirstH2 = firstH2Index >= 0 ? articleHtml.slice(firstH2Index) : articleHtml;
  const tocItems =
    Array.isArray(post.tableOfContents) && post.tableOfContents.length > 0
      ? post.tableOfContents
      : [];
  const publishedDate = new Date(post.dateGmt || post.date);
  const publishedIso = publishedDate.toISOString();
  const publishedLabel = new Intl.DateTimeFormat("th-TH", {
    day: "numeric",
    month: "long",
    year: "numeric"
  }).format(publishedDate);
  const structuredData = {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "BlogPosting",
        "@id": `${canonicalUrl}#article`,
        mainEntityOfPage: { "@type": "WebPage", "@id": canonicalUrl },
        headline: post.title,
        description,
        datePublished: publishedIso,
        inLanguage: "th-TH",
        author: {
          "@type": "Organization",
          "@id": `${siteUrl}/#organization`,
          name: "ทีม KMCP Accounting",
          url: `${siteUrl}/about`
        },
        publisher: { "@id": `${siteUrl}/#organization` },
        image: heroImage ? (heroImage.startsWith("http") ? heroImage : `${siteUrl}${heroImage}`) : `${siteUrl}/images/hero-cover.jpg`
      },
      {
        "@type": "BreadcrumbList",
        itemListElement: [
          { "@type": "ListItem", position: 1, name: "หน้าแรก", item: `${siteUrl}/` },
          { "@type": "ListItem", position: 2, name: "บทความ", item: `${siteUrl}/blog` },
          { "@type": "ListItem", position: 3, name: post.title, item: canonicalUrl }
        ]
      }
    ]
  };

  return (
    <>
      <Seo
        title={post.title}
        description={description}
        path={postPath}
        image={heroImage || undefined}
        imageAlt={heroImage ? heroAlt : undefined}
        type="article"
        jsonLd={structuredData}
      />
      <article className="mx-auto max-w-3xl px-6 py-24 sm:px-8">
        <nav aria-label="เส้นทางนำทาง" className="mb-8">
          <ol className="flex flex-wrap items-center gap-2 text-sm leading-6 text-slate-500">
            <li><Link href="/" className="hover:text-gray-950 hover:underline">หน้าแรก</Link></li>
            <li aria-hidden>/</li>
            <li><Link href="/blog" className="hover:text-gray-950 hover:underline">บทความ</Link></li>
            <li aria-hidden>/</li>
            <li aria-current="page" className="max-w-full truncate font-medium text-gray-900">{post.title}</li>
          </ol>
        </nav>
        <div className="badge mb-6">{categoryLabel}</div>
        <h1 className="text-4xl font-semibold text-gray-900">{post.title}</h1>
        <div className="mt-5 flex flex-wrap items-center gap-x-3 gap-y-1 text-sm leading-6 text-slate-600">
          <span>
            เรียบเรียงโดย <Link href="/about" className="font-medium text-gray-950 hover:underline">ทีม KMCP Accounting</Link>
          </span>
          <span aria-hidden="true">•</span>
          <time dateTime={publishedIso}>เผยแพร่ {publishedLabel}</time>
        </div>
        {heroImage ? (
          <div className="mt-6 overflow-hidden rounded-3xl">
            <Image src={heroImage} alt={heroAlt} width={1200} height={630} className="h-auto w-full object-cover" priority />
          </div>
        ) : null}
        {tocItems.length > 0 ? (
          <nav
            className="mt-6 rounded-3xl border border-brand/20 bg-brand/5 p-6 text-sm text-gray-700"
            aria-label="สารบัญ"
          >
            <p className="text-base font-semibold text-gray-900">สารบัญ</p>
            <ul className="mt-4 space-y-2">
              {tocItems.map((item) => (
                <li key={item.id} className={item.level > 2 ? "pl-4" : ""}>
                  <a href={`#${item.id}`} className="text-brand hover:underline">
                    {item.text}
                  </a>
                </li>
              ))}
            </ul>
          </nav>
        ) : null}
        {leadParagraphHtml ? (
          <p
            className="mt-6 text-lg text-gray-700"
            dangerouslySetInnerHTML={{ __html: leadParagraphHtml }}
          />
        ) : leadParagraphText ? (
          <p className="mt-6 text-lg text-gray-700">{leadParagraphText}</p>
        ) : leadParagraphFallback ? (
          <p className="mt-6 text-lg text-gray-700">{leadParagraphFallback}</p>
        ) : null}
        {contentBeforeFirstH2 ? (
          <div
            className="mt-10 space-y-6 text-base leading-8 text-gray-700 [&_a]:text-[#806500] [&_a:hover]:underline [&_h3]:mt-10 [&_h3]:text-2xl [&_li]:mt-2 [&_ol]:list-decimal [&_ol]:pl-6 [&_p]:mt-6 [&_strong]:font-semibold [&_ul]:list-disc [&_ul]:pl-6 [&_img]:h-auto [&_img]:rounded-3xl [&_img]:max-w-full"
            dangerouslySetInnerHTML={{ __html: contentBeforeFirstH2 }}
          />
        ) : null}
        <BlogServiceCta />
        <div
          className="space-y-6 text-base leading-8 text-gray-700 [&_a]:font-medium [&_a]:text-[#806500] [&_a:hover]:underline [&_h2]:mt-14 [&_h2]:scroll-mt-28 [&_h2]:text-3xl [&_h2]:font-semibold [&_h2]:leading-tight [&_h2]:text-gray-950 [&_h3]:mt-10 [&_h3]:scroll-mt-28 [&_h3]:text-2xl [&_h3]:font-semibold [&_h3]:leading-snug [&_h3]:text-gray-950 [&_li]:mt-2 [&_ol]:list-decimal [&_ol]:pl-6 [&_p]:mt-6 [&_strong]:font-semibold [&_ul]:list-disc [&_ul]:pl-6 [&_img]:h-auto [&_img]:rounded-3xl [&_img]:max-w-full"
          dangerouslySetInnerHTML={{ __html: contentFromFirstH2 }}
        />
        <BlogServiceCta placement="footer" />
      </article>
    </>
  );
}

export const getStaticPaths: GetStaticPaths = async () => {
  return {
    paths: allWordpressPosts
      .filter((post) => post.status === "publish")
      .map((post) => {
        const segments = (Array.isArray(post.slugSegments)
          ? post.slugSegments
          : typeof post.seoSlug === "string"
          ? post.seoSlug.split("/")
          : []
        ).filter((segment): segment is string => typeof segment === "string" && segment.length > 0);
        if (segments.length === 0) {
          segments.push(`post-${post.id}`);
        }
        return { params: { slug: segments } };
      }),
    fallback: false
  };
};

export const getStaticProps: GetStaticProps<PostPageProps> = async ({ params }) => {
  const rawSlug = params?.slug;
  const slugSegments = Array.isArray(rawSlug) ? rawSlug : typeof rawSlug === "string" ? [rawSlug] : [];
  const slugKey = slugSegments.join("/");
  const post = allWordpressPosts.find((item) => {
    if (item.status !== "publish") {
      return false;
    }
    const candidateSegments = Array.isArray(item.slugSegments)
      ? item.slugSegments
      : typeof item.seoSlug === "string"
      ? item.seoSlug.split("/")
      : [];
    return candidateSegments.join("/") === slugKey;
  });
  if (!post) {
    return { notFound: true };
  }
  return {
    props: {
      post
    }
  };
};
